// Seeded finite world: an island of concentric rings, safest at the centre.
// Server-only at runtime (clients get chunks), but pure so tools and tests can run it too.
import { G, MAP_H, MAP_W, OBJ, RINGS, groundSolid, objSolid } from './content.ts';
import { fbm, hash2, mulberry32, rand2 } from './noise.ts';
import { carveDungeons, type Dungeon } from './dungeons.ts';

export type Ruin = { x: number; y: number; w: number; h: number; ring: number };

export type WorldMap = {
  seed: number;
  ground: Uint8Array;
  obj: Uint8Array;
  /** Ring index 0..3 per tile, 255 = sea. */
  ring: Uint8Array;
  ruins: Ruin[];
  spawn: { tx: number; ty: number };
  dungeons: Dungeon[];
};

/** Ring radii (fraction of the half-map), after noise warp. */
export const RING_EDGES = [0.3, 0.54, 0.75];
const SHORE = 0.9;

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

export function ringRadius(x: number, y: number, seed: number): number {
  const dx = (x - MAP_W / 2) / (MAP_W / 2);
  const dy = (y - MAP_H / 2) / (MAP_H / 2);
  return Math.hypot(dx, dy) + (fbm(x / 110, y / 110, seed + 1, 3) - 0.5) * 0.22;
}

export function generate(seed: number): WorldMap {
  const ground = new Uint8Array(MAP_W * MAP_H);
  const obj = new Uint8Array(MAP_W * MAP_H);
  const ring = new Uint8Array(MAP_W * MAP_H);
  const cx = MAP_W / 2;
  const cy = MAP_H / 2;

  for (let y = 0; y < MAP_H; y++) {
    for (let x = 0; x < MAP_W; x++) {
      const i = y * MAP_W + x;
      const r = ringRadius(x, y, seed);
      const edge = Math.min(x, y, MAP_W - 1 - x, MAP_H - 1 - y);
      if (r > SHORE + 0.05 || edge < 4) {
        ground[i] = G.DEEP;
        ring[i] = 255;
        continue;
      }
      if (r > SHORE + 0.02) {
        ground[i] = G.WATER;
        ring[i] = 255;
        continue;
      }
      const k = r < RING_EDGES[0] ? 0 : r < RING_EDGES[1] ? 1 : r < RING_EDGES[2] ? 2 : 3;
      ring[i] = k;
      if (r > SHORE) {
        ground[i] = k === 3 ? G.ASH : G.SAND;
        continue;
      }
      const e = fbm(x / 60, y / 60, seed + 2);
      const m = fbm(x / 40, y / 40, seed + 3);
      const lake = [0.3, 0.34, 0.29, 0.28][k];
      const nearSpawn = Math.hypot(x - cx, y - cy) < 14;
      if (!nearSpawn && e < lake) {
        ground[i] = G.WATER;
        continue;
      }
      if (!nearSpawn && e < lake + 0.018) {
        ground[i] = k === 1 ? G.MUD : k === 3 ? G.ROCK : G.SAND;
        continue;
      }
      const river = fbm(x / 170, y / 170, seed + 4, 3);
      if (!nearSpawn && Math.abs(river - 0.5) < 0.008) {
        ground[i] = G.SHALLOW;
        continue;
      }
      if (k === 0) ground[i] = m > 0.6 ? G.MEADOW : m < 0.38 ? G.DIRT : G.GRASS;
      else if (k === 1) ground[i] = m > 0.58 ? G.MUD : G.FEN;
      else if (k === 2) ground[i] = m > 0.6 ? G.ROCK : m < 0.38 ? G.DIRT : G.HIGH;
      else ground[i] = m > 0.6 ? G.CINDER : m < 0.4 ? G.ROCK : G.ASH;
    }
  }

  // Objects: forests are noise blobs; rock and ore are scattered, richer further out.
  const decor = [OBJ.DECOR_GREEN, OBJ.DECOR_FEN, OBJ.DECOR_HIGH, OBJ.DECOR_ASH];
  for (let y = 0; y < MAP_H; y++) {
    for (let x = 0; x < MAP_W; x++) {
      const i = y * MAP_W + x;
      const g = ground[i];
      if (groundSolid(g) || g === G.SHALLOW || g === G.SAND) continue;
      const k = ring[i];
      const R = RINGS[k];
      const forest = fbm(x / 34, y / 34, seed + 5, 3);
      const dens = clamp01((forest - 0.46) * 3.2) * 0.34 + 0.01;
      const r1 = rand2(x, y, seed + 6);
      let at = dens;
      if (r1 < at) {
        obj[i] = R.trees[hash2(x, y, seed + 7) % R.trees.length];
        continue;
      }
      at += 0.01 + k * 0.002;
      if (r1 < at) {
        obj[i] = pickWeighted(R.ores, rand2(x, y, seed + 8));
        continue;
      }
      at += k <= 1 ? 0.004 : 0.001;
      if (r1 < at) {
        obj[i] = OBJ.BERRY_BUSH;
        continue;
      }
      at += 0.005;
      if (r1 < at) {
        obj[i] = k === 3 ? decor[3] : OBJ.BUSH;
        continue;
      }
      at += 0.002;
      if (r1 < at) {
        obj[i] = OBJ.LOG;
        continue;
      }
      at += 0.05;
      if (r1 < at) obj[i] = decor[k];
    }
  }

  const ruins = placeRuins(seed, ground, obj, ring);
  const dungeons = carveDungeons(seed, ground, obj, ring, ruins);

  // Spawn: a cleared stone circle at the centre.
  for (let y = cy - 8; y <= cy + 8; y++) {
    for (let x = cx - 8; x <= cx + 8; x++) {
      const d = Math.hypot(x - cx, y - cy);
      const i = y * MAP_W + x;
      if (d <= 7.5) obj[i] = 0;
      if (d <= 2.6) ground[i] = G.STONE;
      else if (d <= 7.5 && groundSolid(ground[i])) ground[i] = G.GRASS;
    }
  }

  return { seed, ground, obj, ring, ruins, spawn: { tx: cx, ty: cy + 3 }, dungeons };
}

function pickWeighted(list: [number, number][], r: number): number {
  const total = list.reduce((s, [, w]) => s + w, 0);
  let t = r * total;
  for (const [id, w] of list) {
    t -= w;
    if (t <= 0) return id;
  }
  return list[list.length - 1][0];
}

/** Ruined buildings to claim in stage 1. For now: stone floor, broken walls, a door on the south. */
function placeRuins(seed: number, ground: Uint8Array, obj: Uint8Array, ring: Uint8Array): Ruin[] {
  const rng = mulberry32(seed + 9);
  const want = [6, 8, 8, 8];
  const out: Ruin[] = [];
  for (let k = 0; k < 4; k++) {
    let placed = 0;
    for (let tries = 0; tries < 4000 && placed < want[k]; tries++) {
      const w = 7 + Math.floor(rng() * 4);
      const h = 6 + Math.floor(rng() * 3);
      const x = 8 + Math.floor(rng() * (MAP_W - w - 16));
      const y = 8 + Math.floor(rng() * (MAP_H - h - 16));
      const mid = (y + (h >> 1)) * MAP_W + x + (w >> 1);
      if (ring[mid] !== k) continue;
      if (Math.hypot(x - MAP_W / 2, y - MAP_H / 2) < 40) continue;
      if (out.some((r) => Math.abs(r.x - x) < 36 && Math.abs(r.y - y) < 36)) continue;
      let wet = 0;
      for (let yy = y - 1; yy <= y + h; yy++) for (let xx = x - 1; xx <= x + w; xx++) if (groundSolid(ground[yy * MAP_W + xx]) || ring[yy * MAP_W + xx] === 255) wet++;
      if (wet > 0) continue;
      for (let yy = y - 2; yy <= y + h + 1; yy++) {
        for (let xx = x - 2; xx <= x + w + 1; xx++) {
          const i = yy * MAP_W + xx;
          obj[i] = 0;
          const inside = xx >= x && xx < x + w && yy >= y && yy < y + h;
          if (!inside) continue;
          ground[i] = G.STONE;
          const edgeX = xx === x || xx === x + w - 1;
          const edgeY = yy === y || yy === y + h - 1;
          const corner = edgeX && edgeY;
          const door = yy === y + h - 1 && Math.abs(xx - (x + (w >> 1))) <= 1;
          if (corner) obj[i] = OBJ.RUIN_PILLAR;
          else if ((edgeX || edgeY) && !door) obj[i] = rng() < 0.68 ? OBJ.RUIN_WALL : rng() < 0.5 ? OBJ.RUBBLE : 0;
          else if (!door && rng() < 0.1) obj[i] = OBJ.RUBBLE;
        }
      }
      out.push({ x, y, w, h, ring: k });
      placed++;
    }
  }
  return out;
}

export function walkable(map: WorldMap, tx: number, ty: number): boolean {
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return false;
  const i = ty * MAP_W + tx;
  return !groundSolid(map.ground[i]) && !objSolid(map.obj[i]);
}
